import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { adminApi, apiError } from "@/lib/api";
import { AdminNav } from "@/components/AdminNav";
import { toast } from "sonner";
import {
  ArrowLeft, Phone, MapPin, MessageSquare, ChevronRight, Users, CalendarClock,
  Star, Search, StickyNote, Home, Briefcase, Plus, Pencil, Trash2, X, Check,
  Tag as TagIcon, Archive, Settings2,
} from "lucide-react";

function fmt(d) { return d ? new Date(d).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" }) : "—"; }
function fmtDate(d) { return d ? new Date(d).toLocaleDateString("en-NZ", { dateStyle: "medium" }) : "—"; }

const TAG_COLORS = ["#2563eb", "#7c3aed", "#0891b2", "#ea580c", "#4f46e5", "#0d9488",
  "#db2777", "#65a30d", "#9333ea", "#16a34a", "#dc2626", "#64748b", "#ca8a04", "#0ea5e9"];

function Chip({ tag }) {
  return (
    <span data-testid={`tag-chip-${tag.key}`} className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium text-white"
      style={{ backgroundColor: tag.color }}>
      {tag.label}
    </span>
  );
}

export default function AdminCustomers() {
  const nav = useNavigate();
  const { id } = useParams();
  return id ? <CustomerDetail id={id} nav={nav} /> : <CustomerList nav={nav} />;
}

/* ---------------- LIST ---------------- */
function CustomerList({ nav }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");
  const [priority, setPriority] = useState(false);
  const [status, setStatus] = useState("active");
  const [tags, setTags] = useState({ standard: [], custom: [] });
  const [showTagMgr, setShowTagMgr] = useState(false);

  const load = useCallback(async () => {
    if (!localStorage.getItem("mrshu_admin_token")) { nav("/admin/login"); return; }
    setLoading(true);
    try {
      const params = { q, tag, priority: priority ? "true" : "", status };
      const { data } = await adminApi.get("/admin/customers", { params });
      setRows(data);
    } catch (e) { if ([401, 403].includes(e?.response?.status)) nav("/admin/login"); }
    finally { setLoading(false); }
  }, [q, tag, priority, status, nav]);

  const loadTags = useCallback(async () => {
    try { const { data } = await adminApi.get("/admin/tags"); setTags(data); } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { loadTags(); }, [loadTags]);

  const activeCustom = tags.custom.filter((t) => !t.archived);
  const allTags = [...tags.standard, ...activeCustom];

  return (
    <div className="min-h-screen bg-background">
      <AdminNav active="customers" />
      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-8">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-display text-2xl font-bold text-primary flex items-center gap-2"><Users className="h-6 w-6" /> Customers</h2>
          <button data-testid="manage-tags-btn" onClick={() => setShowTagMgr(true)}
            className="flex items-center gap-2 rounded-full border border-border bg-white px-3 py-2 text-sm font-medium hover:border-primary/40">
            <Settings2 className="h-4 w-4" /> Manage tags
          </button>
        </div>

        {/* Search + filters */}
        <div className="mt-5 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input data-testid="customer-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, phone, address, business, or tag…"
              className="w-full rounded-full border border-border bg-white pl-10 pr-4 py-2.5 text-sm outline-none focus:border-primary" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select data-testid="filter-tag" value={tag} onChange={(e) => setTag(e.target.value)}
              className="rounded-full border border-border bg-white px-3 py-1.5 text-sm outline-none focus:border-primary">
              <option value="">All tags</option>
              {allTags.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            <button data-testid="filter-priority" onClick={() => setPriority((p) => !p)}
              className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${priority ? "bg-amber-500 text-white" : "border border-border bg-white hover:border-primary/40"}`}>
              <Star className={`h-3.5 w-3.5 ${priority ? "fill-white" : ""}`} /> Priority
            </button>
            <div className="flex rounded-full border border-border bg-white p-0.5 text-sm">
              {["active", "archived", "all"].map((s) => (
                <button key={s} data-testid={`filter-status-${s}`} onClick={() => setStatus(s)}
                  className={`rounded-full px-3 py-1 capitalize transition-colors ${status === s ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{s}</button>
              ))}
            </div>
          </div>
        </div>

        {loading ? <p className="text-muted-foreground mt-6">Loading…</p> : rows.length === 0 ? <p className="text-muted-foreground mt-6" data-testid="customers-empty">No customers match.</p> : (
          <div className="mt-6 space-y-3" data-testid="customers-list">
            {rows.map((c) => <CustomerCard key={c.id} c={c} nav={nav} />)}
          </div>
        )}
      </main>

      {showTagMgr && <TagManager tags={tags} onClose={() => setShowTagMgr(false)} reload={() => { loadTags(); load(); }} />}
    </div>
  );
}

function CustomerCard({ c, nav }) {
  const shown = c.tags.slice(0, 3);
  const extra = c.tags.length - shown.length;
  const sub = c.business_name || c.business_description || c.summary;
  return (
    <button data-testid={`customer-row-${c.id}`} onClick={() => nav(`/admin/customers/${c.id}`)}
      className={`flex w-full items-start gap-4 rounded-2xl border bg-white p-4 shadow-sm hover:border-primary/40 transition-colors text-left ${c.priority ? "border-amber-400" : "border-border"} ${c.archived ? "opacity-70" : ""}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-display font-bold text-foreground">{c.name}</p>
          {c.priority && <span data-testid={`priority-badge-${c.id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700"><Star className="h-3 w-3 fill-amber-500 text-amber-500" /> Priority</span>}
          {c.archived && <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">Archived</span>}
        </div>
        {sub && <p className="text-sm text-foreground/80 mt-0.5 truncate">{sub}</p>}
        <p className="text-sm text-muted-foreground mt-0.5 flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {c.phone}</p>
        {c.address && <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1 truncate"><MapPin className="h-3.5 w-3.5 shrink-0" /> {c.address}</p>}
        {c.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {shown.map((t) => <Chip key={t.key} tag={t} />)}
            {extra > 0 && <span className="text-xs text-muted-foreground">+ {extra} more</span>}
          </div>
        )}
      </div>
      <div className="text-right text-xs space-y-1 shrink-0">
        <p className="font-semibold text-primary">{c.requests} requests</p>
        <p className="text-muted-foreground">{c.active_requests} active</p>
        {c.note_count > 0 && <p className="text-muted-foreground flex items-center justify-end gap-1"><StickyNote className="h-3 w-3" /> {c.note_count}</p>}
        {c.unread_messages > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-destructive px-2 py-0.5 font-bold text-destructive-foreground"><MessageSquare className="h-3 w-3" /> {c.unread_messages}</span>}
        <p className="text-muted-foreground">{fmtDate(c.last_activity)}</p>
      </div>
      <ChevronRight className="h-5 w-5 text-primary/50 self-center shrink-0" />
    </button>
  );
}

/* ---------------- DETAIL ---------------- */
function CustomerDetail({ id, nav }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tags, setTags] = useState({ standard: [], custom: [] });
  const [showTagMgr, setShowTagMgr] = useState(false);

  const load = useCallback(async () => {
    if (!localStorage.getItem("mrshu_admin_token")) { nav("/admin/login"); return; }
    try { const { data } = await adminApi.get(`/admin/customers/${id}`); setDetail(data); }
    catch (e) { if ([401, 403].includes(e?.response?.status)) nav("/admin/login"); }
    finally { setLoading(false); }
  }, [id, nav]);
  const loadTags = useCallback(async () => {
    try { const { data } = await adminApi.get("/admin/tags"); setTags(data); } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { load(); loadTags(); }, [load, loadTags]);

  if (loading || !detail) return (
    <div className="min-h-screen bg-background"><AdminNav active="customers" /><main className="max-w-5xl mx-auto px-4 sm:px-8 py-8"><p className="text-muted-foreground">Loading…</p></main></div>
  );
  const c = detail.customer;

  return (
    <div className="min-h-screen bg-background">
      <AdminNav active="customers" />
      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-8">
        <button data-testid="cust-back-btn" onClick={() => nav("/admin/customers")} className="flex items-center gap-2 text-primary font-medium mb-6"><ArrowLeft className="h-5 w-5" /> Customers</button>

        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="font-display text-2xl font-bold text-primary">{c.name}</h2>
          {c.priority && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700"><Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" /> Priority</span>}
          {c.archived && <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">Archived</span>}
        </div>
        <div className="mt-2 flex flex-wrap gap-4 text-sm text-foreground">
          <span className="flex items-center gap-1"><Phone className="h-4 w-4 text-primary/70" /> <a href={`tel:${c.phone}`} className="hover:underline">{c.phone}</a></span>
          <span className="flex items-center gap-1"><MapPin className="h-4 w-4 text-primary/70" /> {c.address}</span>
          <span className="text-muted-foreground">Last activity: {fmt(c.last_activity)}</span>
        </div>

        <BusinessSection c={c} id={id} reload={load} />
        <TagsSection c={c} id={id} tags={tags} reload={load} openMgr={() => setShowTagMgr(true)} />
        <NotesSection id={id} notes={detail.notes} reload={load} />
        <PropertiesSection properties={detail.properties} />

        {detail.appointments.length > 0 && (
          <div className="mt-6">
            <p className="font-semibold text-primary flex items-center gap-2"><CalendarClock className="h-4 w-4" /> Upcoming / proposed appointments</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {detail.appointments.map((a, i) => <span key={i} className="rounded-full bg-secondary px-3 py-1 text-xs">{a.ref_number}: {a.type} · {a.state} · {a.date || "?"} {a.slot || ""}</span>)}
            </div>
          </div>
        )}

        <RequestsSection title={`Active requests (${detail.active.length})`} jobs={detail.active} nav={nav} testId="cust-active" />
        <RequestsSection title={`Past requests (${detail.past.length})`} jobs={detail.past} nav={nav} testId="cust-past" />
      </main>
      {showTagMgr && <TagManager tags={tags} onClose={() => setShowTagMgr(false)} reload={() => { loadTags(); load(); }} />}
    </div>
  );
}

function BusinessSection({ c, id, reload }) {
  const [edit, setEdit] = useState(false);
  const [bn, setBn] = useState(c.business_name || "");
  const [bd, setBd] = useState(c.business_description || "");
  const [sm, setSm] = useState(c.summary || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.patch(`/admin/customers/${id}`, { business_name: bn, business_description: bd, summary: sm });
      toast.success("Saved");
      setEdit(false); reload();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const toggle = async (field, value) => {
    try { await adminApi.patch(`/admin/customers/${id}`, { [field]: value }); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="mt-6 rounded-2xl border border-border bg-white p-5" data-testid="business-section">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-primary flex items-center gap-2"><Briefcase className="h-4 w-4" /> Business information</p>
        <div className="flex items-center gap-2">
          <button data-testid="toggle-priority-btn" onClick={() => toggle("priority", !c.priority)}
            className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium ${c.priority ? "bg-amber-500 text-white" : "border border-border hover:border-primary/40"}`}>
            <Star className={`h-3.5 w-3.5 ${c.priority ? "fill-white" : ""}`} /> {c.priority ? "Priority" : "Mark priority"}
          </button>
          <button data-testid="toggle-archive-btn" onClick={() => toggle("archived", !c.archived)}
            className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium ${c.archived ? "bg-muted text-foreground" : "border border-border hover:border-primary/40"}`}>
            <Archive className="h-3.5 w-3.5" /> {c.archived ? "Reactivate" : "Archive"}
          </button>
          {!edit && <button data-testid="edit-business-btn" onClick={() => setEdit(true)} className="flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-medium hover:border-primary/40"><Pencil className="h-3.5 w-3.5" /> Edit</button>}
        </div>
      </div>
      {edit ? (
        <div className="mt-4 space-y-3">
          <Field label="Business name"><input data-testid="input-business-name" value={bn} onChange={(e) => setBn(e.target.value)} className="w-full rounded-lg border border-border px-3 py-2 text-sm outline-none focus:border-primary" placeholder="Optional" /></Field>
          <Field label="Business / occupation description"><textarea data-testid="input-business-desc" value={bd} onChange={(e) => setBd(e.target.value)} rows={2} className="w-full rounded-lg border border-border px-3 py-2 text-sm outline-none focus:border-primary" placeholder="Optional" /></Field>
          <Field label="Short customer summary"><textarea data-testid="input-summary" value={sm} onChange={(e) => setSm(e.target.value)} rows={2} className="w-full rounded-lg border border-border px-3 py-2 text-sm outline-none focus:border-primary" placeholder="Optional" /></Field>
          <div className="flex gap-2">
            <button data-testid="save-business-btn" disabled={saving} onClick={save} className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">Save</button>
            <button onClick={() => { setEdit(false); setBn(c.business_name || ""); setBd(c.business_description || ""); setSm(c.summary || ""); }} className="rounded-full border border-border px-4 py-2 text-sm">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="mt-3 space-y-2 text-sm">
          <p><span className="text-muted-foreground">Business:</span> {c.business_name || <span className="text-muted-foreground">—</span>}</p>
          <p><span className="text-muted-foreground">Description:</span> {c.business_description || <span className="text-muted-foreground">—</span>}</p>
          <p><span className="text-muted-foreground">Summary:</span> {c.summary || <span className="text-muted-foreground">—</span>}</p>
        </div>
      )}
    </div>
  );
}

function TagsSection({ c, id, tags, reload, openMgr }) {
  const [saving, setSaving] = useState(false);
  const active = c.tags.map((t) => t.key);
  const activeCustom = tags.custom.filter((t) => !t.archived);
  const all = [...tags.standard, ...activeCustom];

  const toggle = async (key) => {
    const next = active.includes(key) ? active.filter((k) => k !== key) : [...active, key];
    setSaving(true);
    try { await adminApi.patch(`/admin/customers/${id}`, { tags: next }); reload(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="mt-6 rounded-2xl border border-border bg-white p-5" data-testid="tags-section">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-primary flex items-center gap-2"><TagIcon className="h-4 w-4" /> Tags</p>
        <button data-testid="detail-manage-tags-btn" onClick={openMgr} className="flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-medium hover:border-primary/40"><Settings2 className="h-3.5 w-3.5" /> Manage custom tags</button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="tag-toggles">
        {all.map((t) => {
          const on = active.includes(t.key);
          return (
            <button key={t.key} data-testid={`tag-toggle-${t.key}`} disabled={saving} onClick={() => toggle(t.key)}
              className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-all border-2"
              style={on ? { backgroundColor: t.color, color: "#fff", borderColor: t.color } : { borderColor: t.color, color: t.color }}>
              {on && <Check className="h-3 w-3" />} {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NotesSection({ id, notes, reload }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");
  const [delId, setDelId] = useState(null);

  const add = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try { await adminApi.post(`/admin/customers/${id}/notes`, { text }); setText(""); reload(); toast.success("Note added"); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const saveEdit = async () => {
    try { await adminApi.patch(`/admin/customers/${id}/notes/${editId}`, { text: editText }); setEditId(null); reload(); toast.success("Note updated"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async () => {
    try { await adminApi.delete(`/admin/customers/${id}/notes/${delId}`); setDelId(null); reload(); toast.success("Note deleted"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="mt-6 rounded-2xl border border-border bg-white p-5" data-testid="notes-section">
      <p className="font-semibold text-primary flex items-center gap-2"><StickyNote className="h-4 w-4" /> Private customer notes</p>
      <div className="mt-3 flex gap-2">
        <textarea data-testid="note-input" value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Add a private note about this customer…"
          className="flex-1 rounded-lg border border-border px-3 py-2 text-sm outline-none focus:border-primary" />
        <button data-testid="add-note-btn" disabled={saving || !text.trim()} onClick={add} className="self-start rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60 flex items-center gap-1"><Plus className="h-4 w-4" /> Add</button>
      </div>
      <div className="mt-4 space-y-3" data-testid="notes-list">
        {notes.length === 0 ? <p className="text-sm text-muted-foreground">No notes yet.</p> : notes.map((n) => (
          <div key={n.id} data-testid={`note-${n.id}`} className="rounded-xl border border-border bg-secondary/40 p-3">
            {editId === n.id ? (
              <div className="space-y-2">
                <textarea data-testid="edit-note-input" value={editText} onChange={(e) => setEditText(e.target.value)} rows={2} className="w-full rounded-lg border border-border px-3 py-2 text-sm outline-none focus:border-primary" />
                <div className="flex gap-2">
                  <button data-testid="save-note-edit-btn" onClick={saveEdit} className="rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground">Save</button>
                  <button onClick={() => setEditId(null)} className="rounded-full border border-border px-3 py-1.5 text-xs">Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm whitespace-pre-wrap">{n.text}</p>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{fmt(n.created_at)}{n.updated_at ? " · edited" : ""}</span>
                  <div className="flex gap-1">
                    <button data-testid={`edit-note-btn-${n.id}`} onClick={() => { setEditId(n.id); setEditText(n.text); }} className="rounded-full p-1.5 hover:bg-white"><Pencil className="h-3.5 w-3.5 text-muted-foreground" /></button>
                    <button data-testid={`delete-note-btn-${n.id}`} onClick={() => setDelId(n.id)} className="rounded-full p-1.5 hover:bg-white"><Trash2 className="h-3.5 w-3.5 text-destructive" /></button>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
      {delId && (
        <Modal onClose={() => setDelId(null)}>
          <p className="font-semibold text-foreground">Delete this note?</p>
          <p className="text-sm text-muted-foreground mt-1">This can't be undone.</p>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setDelId(null)} className="rounded-full border border-border px-4 py-2 text-sm">Cancel</button>
            <button data-testid="confirm-delete-note-btn" onClick={del} className="rounded-full bg-destructive px-4 py-2 text-sm font-semibold text-destructive-foreground">Delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function PropertiesSection({ properties }) {
  const active = properties.filter((p) => !p.archived);
  const archived = properties.filter((p) => p.archived);
  return (
    <div className="mt-6 rounded-2xl border border-border bg-white p-5" data-testid="properties-section">
      <p className="font-semibold text-primary flex items-center gap-2"><Home className="h-4 w-4" /> Saved properties ({active.length})</p>
      {active.length === 0 ? <p className="text-sm text-muted-foreground mt-2">None.</p> : (
        <div className="mt-3 space-y-2">
          {active.map((p) => (
            <div key={p.id} className="rounded-xl border border-border p-3 text-sm">
              <div className="flex items-center gap-2">
                <span className="font-semibold">{p.nickname}</span>
                <span className="rounded-full bg-secondary px-2 py-0.5 text-xs">{p.type}</span>
                {p.is_default && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">Default</span>}
              </div>
              <p className="text-muted-foreground mt-1">{p.address}{p.unit ? `, ${p.unit}` : ""}</p>
              {p.access_notes && <p className="text-xs text-muted-foreground mt-1">Access: {p.access_notes}</p>}
            </div>
          ))}
        </div>
      )}
      {archived.length > 0 && <p className="text-xs text-muted-foreground mt-2">{archived.length} archived</p>}
    </div>
  );
}

function RequestsSection({ title, jobs, nav, testId }) {
  return (
    <div className="mt-6" data-testid={testId}>
      <p className="font-semibold text-primary">{title}</p>
      {jobs.length === 0 ? <p className="text-sm text-muted-foreground mt-1">None.</p> : (
        <div className="mt-2 space-y-2">
          {jobs.map((j) => (
            <button key={j.id} onClick={() => nav(`/admin/jobs/${j.id}`)} className="flex w-full items-center justify-between rounded-xl border border-border bg-white p-3 text-left text-sm hover:border-primary/40">
              <span><span className="font-semibold text-primary">{j.ref_number}</span> · {j.work_type}</span>
              <span className="flex items-center gap-2">
                {j.unread_messages > 0 && <span className="rounded-full bg-destructive px-2 py-0.5 text-xs font-bold text-destructive-foreground">{j.unread_messages}</span>}
                <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs">{j.status}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- TAG MANAGER ---------------- */
function TagManager({ tags, onClose, reload }) {
  const [label, setLabel] = useState("");
  const [color, setColor] = useState(TAG_COLORS[0]);
  const [editId, setEditId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [editColor, setEditColor] = useState("");

  const create = async () => {
    if (!label.trim()) return;
    try { await adminApi.post("/admin/tags", { label, color }); setLabel(""); reload(); toast.success("Tag created"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveEdit = async () => {
    try { await adminApi.patch(`/admin/tags/${editId}`, { label: editLabel, color: editColor }); setEditId(null); reload(); toast.success("Tag updated"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const archive = async (t) => {
    try { await adminApi.post(`/admin/tags/${t.key}/archive`); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Modal onClose={onClose} wide>
      <div className="flex items-center justify-between">
        <p className="font-display text-lg font-bold text-primary">Manage custom tags</p>
        <button onClick={onClose} className="rounded-full p-1.5 hover:bg-secondary"><X className="h-4 w-4" /></button>
      </div>

      <div className="mt-4 rounded-xl border border-border p-3">
        <p className="text-sm font-semibold mb-2">Create custom tag</p>
        <div className="flex flex-wrap items-center gap-2">
          <input data-testid="new-tag-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder='e.g. "Cake Shop"' className="flex-1 min-w-[140px] rounded-lg border border-border px-3 py-2 text-sm outline-none focus:border-primary" />
          <ColorPicker value={color} onChange={setColor} testId="new-tag" />
          <button data-testid="create-tag-btn" onClick={create} disabled={!label.trim()} className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">Create</button>
        </div>
      </div>

      <div className="mt-4 space-y-2 max-h-72 overflow-auto" data-testid="custom-tag-list">
        {tags.custom.length === 0 ? <p className="text-sm text-muted-foreground">No custom tags yet.</p> : tags.custom.map((t) => (
          <div key={t.key} data-testid={`custom-tag-${t.key}`} className={`rounded-xl border border-border p-3 ${t.archived ? "opacity-60" : ""}`}>
            {editId === t.key ? (
              <div className="flex flex-wrap items-center gap-2">
                <input data-testid="edit-tag-label" value={editLabel} onChange={(e) => setEditLabel(e.target.value)} className="flex-1 min-w-[120px] rounded-lg border border-border px-3 py-1.5 text-sm outline-none focus:border-primary" />
                <ColorPicker value={editColor} onChange={setEditColor} testId="edit-tag" />
                <button data-testid="save-tag-edit-btn" onClick={saveEdit} className="rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground">Save</button>
                <button onClick={() => setEditId(null)} className="rounded-full border border-border px-3 py-1.5 text-xs">Cancel</button>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium text-white" style={{ backgroundColor: t.color }}>{t.label}</span>
                <div className="flex items-center gap-1">
                  {t.archived && <span className="text-xs text-muted-foreground mr-1">archived</span>}
                  <button data-testid={`edit-tag-btn-${t.key}`} onClick={() => { setEditId(t.key); setEditLabel(t.label); setEditColor(t.color); }} className="rounded-full p-1.5 hover:bg-secondary"><Pencil className="h-3.5 w-3.5" /></button>
                  <button data-testid={`archive-tag-btn-${t.key}`} onClick={() => archive(t)} className="rounded-full p-1.5 hover:bg-secondary"><Archive className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
}

function ColorPicker({ value, onChange, testId }) {
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={`${testId}-colors`}>
      {TAG_COLORS.map((c) => (
        <button key={c} type="button" data-testid={`${testId}-color-${c}`} onClick={() => onChange(c)}
          className={`h-6 w-6 rounded-full border-2 ${value === c ? "border-foreground" : "border-transparent"}`} style={{ backgroundColor: c }} />
      ))}
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="text-xs font-medium text-muted-foreground">{label}</span><div className="mt-1">{children}</div></label>;
}

function Modal({ children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className={`w-full ${wide ? "max-w-lg" : "max-w-sm"} rounded-2xl bg-white p-6 shadow-2xl`} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}
